import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { fmtDateTime, fmtDate } from '@/lib/format';

export interface Contact360 {
  contact: {
    id: string;
    ffUserId: string | null;
    email: string;
    firstName: string | null;
    lastName: string | null;
    businessName: string | null;
    userFunction: string | null;
    organizationName: string | null;
    tier: string;
    subStatus: string | null;
    currentPeriodEnd: string | null;
    source: string;
    tags: string[];
    suppressedReason: string | null;
    unsubscribedAt: string | null;
    ffCreatedAt: string | null;
    lastActivityAt: string | null;
    lastSyncedAt: string | null;
    usage30d: Record<string, number> | null;
    creditsUsedPct: number | null;
    isApproved: boolean;
    isActive: boolean;
    emailVerified: boolean;
    profileCompleted: boolean;
  };
  segments: { id: string; name: string; kind: string }[];
  suppression: { reason: string; source: string | null; createdAt: string } | null;
  subscription: {
    tier: string;
    tierDisplay: string;
    status: string;
    source: string;
    trialActive: boolean;
    trialTier: string | null;
    cancelAtPeriodEnd: boolean;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    canceledAt: string | null;
    trialEnd: string | null;
    stripePriceId: string | null;
    stripeSubscriptionId: string | null;
    stripeCustomerId: string | null;
    organizationName: string | null;
  } | null;
  ffUser: {
    id: string;
    role: string | null;
    country: string | null;
    website: string | null;
    instagram: string | null;
    hasCompletedOnboarding: boolean | null;
    requested: Record<string, string | null>;
  } | null;
  credits: {
    scope: string;
    monthly: { allotted: number; used: number };
    standard: { allotted: number | null; used: number };
    business: { allotted: number; used: number };
    pool: number;
    purchased: number;
    trial: number;
    nextReset: string | null;
  } | null;
  usage: {
    byFeature: Record<string, { countAllTime: number; count30d: number; lastAt: string | null }>;
    images: { bySource: Record<string, { generations: number; images: number; lastAt: string | null }>; total: number };
    conversations: { byAgent: Record<string, { count: number; lastAt: string | null }>; total: number };
  } | null;
  recentPrompts: { id: string; text: string; at: string | null; agentType: string | null; conversationTitle: string | null }[];
  emails: {
    id: string;
    origin: 'crm' | 'facetflow';
    subject: string;
    kind: string | null;
    status: string;
    sentAt: string | null;
    openedAt: string | null;
    openCount: number;
    clickCount: number;
    sandbox: boolean;
    isTest: boolean;
  }[];
  audit: { id: string; type: string; source: string; occurredAt: string; payload: Record<string, unknown> | null }[];
  flows: { id: string; flowId: string; status: string; stepIndex: number; nextRunAt: string | null; createdAt: string }[];
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-all">{value ?? "—"}</dd>
    </>
  );
}

export function ContactTabs({ data }: { data: Contact360 }) {
  const { contact, subscription, credits, usage, ffUser } = data;

  return (
    <Tabs defaultValue="overview" className="rounded-lg border bg-white p-4">
      <TabsList>
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="usage">Usage</TabsTrigger>
        <TabsTrigger value="prompts">Prompts</TabsTrigger>
        <TabsTrigger value="emails">Emails ({data.emails.length})</TabsTrigger>
        <TabsTrigger value="audit">Audit ({data.audit.length})</TabsTrigger>
        <TabsTrigger value="flows">Flows ({data.flows.length})</TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="grid gap-6 pt-4 text-sm md:grid-cols-2">
        <section>
          <h3 className="mb-2 text-base">Subscription</h3>
          {subscription ? (
            <dl className="grid grid-cols-[140px_1fr] gap-y-1">
              <Row label="Plan" value={`${subscription.tierDisplay} (${subscription.tier})`} />
              <Row label="Status" value={subscription.status} />
              <Row label="Billed via" value={subscription.source === 'organization' ? `organization · ${subscription.organizationName ?? ''}` : subscription.source} />
              <Row label="Period" value={`${fmtDate(subscription.currentPeriodStart)} → ${fmtDate(subscription.currentPeriodEnd)}`} />
              <Row label="Cancel at period end" value={subscription.cancelAtPeriodEnd ? 'yes' : 'no'} />
              <Row label="Canceled at" value={fmtDate(subscription.canceledAt)} />
              <Row label="Trial" value={subscription.trialActive ? `${subscription.trialTier} until ${fmtDate(subscription.trialEnd)}` : subscription.trialEnd ? `ended ${fmtDate(subscription.trialEnd)}` : '—'} />
              <Row label="Stripe price" value={subscription.stripePriceId} />
              <Row
                label="Stripe customer"
                value={
                  subscription.stripeCustomerId ? (
                    <a className="ff-link" href={`https://dashboard.stripe.com/customers/${subscription.stripeCustomerId}`} target="_blank" rel="noreferrer">
                      {subscription.stripeCustomerId}
                    </a>
                  ) : null
                }
              />
            </dl>
          ) : (
            <p className="text-muted-foreground">Not a Facet Flow user yet (imported lead).</p>
          )}
        </section>

        <section>
          <h3 className="mb-2 text-base">Credits</h3>
          {credits ? (
            <dl className="grid grid-cols-[140px_1fr] gap-y-1">
              <Row label="Scope" value={credits.scope} />
              <Row label="Monthly (pro)" value={`${credits.monthly.used} / ${credits.monthly.allotted} used${contact.creditsUsedPct != null ? ` (${contact.creditsUsedPct}%)` : ''}`} />
              <Row label="Standard" value={`${credits.standard.used} / ${credits.standard.allotted ?? '∞'}`} />
              <Row label="Business" value={`${credits.business.used} / ${credits.business.allotted}`} />
              <Row label="Pool" value={credits.pool} />
              <Row label="Purchased" value={credits.purchased} />
              <Row label="Trial" value={credits.trial} />
              <Row label="Next reset" value={fmtDate(credits.nextReset)} />
            </dl>
          ) : (
            <p className="text-muted-foreground">No credit record.</p>
          )}
        </section>

        <section>
          <h3 className="mb-2 text-base">Account</h3>
          <dl className="grid grid-cols-[140px_1fr] gap-y-1">
            <Row label="FF user id" value={contact.ffUserId} />
            <Row label="Approved" value={contact.isApproved ? 'yes' : 'no'} />
            <Row label="Active" value={contact.isActive ? 'yes' : 'no'} />
            <Row label="Email verified" value={contact.emailVerified ? 'yes' : 'no'} />
            <Row label="Profile completed" value={contact.profileCompleted ? 'yes' : 'no'} />
            <Row label="Onboarding" value={ffUser?.hasCompletedOnboarding ? 'completed' : 'not completed'} />
            <Row label="Country" value={ffUser?.country} />
            <Row label="Website" value={ffUser?.website} />
            <Row label="Instagram" value={ffUser?.instagram} />
          </dl>
        </section>

        <section>
          <h3 className="mb-2 text-base">Segments & access requests</h3>
          <div className="mb-3 flex flex-wrap gap-1">
            {data.segments.length === 0 && <span className="text-muted-foreground">No segments</span>}
            {data.segments.map((s) => (
              <span key={s.id} className="rounded bg-muted px-1.5 py-0.5 text-xs">
                {s.name} ({s.kind})
              </span>
            ))}
          </div>
          {ffUser && (
            <dl className="grid grid-cols-[140px_1fr] gap-y-1">
              {Object.entries(ffUser.requested).map(([k, v]) => (
                <Row key={k} label={`Requested ${k}`} value={fmtDate(v)} />
              ))}
            </dl>
          )}
          {data.suppression && (
            <p className="mt-3 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800">
              Suppressed: {data.suppression.reason} ({data.suppression.source ?? 'unknown'}) since {fmtDate(data.suppression.createdAt)}
            </p>
          )}
        </section>
      </TabsContent>

      <TabsContent value="usage" className="space-y-6 pt-4 text-sm">
        {!usage ? (
          <p className="text-muted-foreground">No product usage (not a Facet Flow user).</p>
        ) : (
          <>
            <section>
              <h3 className="mb-2 text-base">By generation type (credit usage)</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Last 30 days</th>
                    <th>All time</th>
                    <th>Last used</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(usage.byFeature).length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-muted-foreground">
                        No credit usage yet
                      </td>
                    </tr>
                  )}
                  {Object.entries(usage.byFeature)
                    .sort((a, b) => b[1].countAllTime - a[1].countAllTime)
                    .map(([feature, v]) => (
                      <tr key={feature}>
                        <td>{feature}</td>
                        <td>{v.count30d}</td>
                        <td>{v.countAllTime}</td>
                        <td>{fmtDateTime(v.lastAt)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </section>
            <section className="grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="mb-2 text-base">Image generations ({usage.images.total} images)</h3>
                <dl className="grid grid-cols-[140px_1fr] gap-y-1">
                  {Object.entries(usage.images.bySource).map(([source, v]) => (
                    <Row key={source} label={source} value={`${v.generations} runs · ${v.images} images · ${fmtDate(v.lastAt)}`} />
                  ))}
                </dl>
              </div>
              <div>
                <h3 className="mb-2 text-base">Conversations ({usage.conversations.total})</h3>
                <dl className="grid grid-cols-[140px_1fr] gap-y-1">
                  {Object.entries(usage.conversations.byAgent).map(([agent, v]) => (
                    <Row key={agent} label={agent} value={`${v.count} · last ${fmtDate(v.lastAt)}`} />
                  ))}
                </dl>
              </div>
            </section>
            {contact.usage30d && (
              <section>
                <h3 className="mb-2 text-base">Rolled-up 30d snapshot (what segments see)</h3>
                <code className="block rounded bg-muted p-2 text-xs">{JSON.stringify(contact.usage30d)}</code>
              </section>
            )}
          </>
        )}
      </TabsContent>

      <TabsContent value="prompts" className="space-y-3 pt-4 text-sm">
        <p className="text-xs text-muted-foreground">Shown in-app only. Never exported, emailed, or sent to analytics.</p>
        {data.recentPrompts.length === 0 && <p className="text-muted-foreground">No prompts.</p>}
        {data.recentPrompts.map((p) => (
          <div key={p.id} className="rounded border p-3">
            <div className="mb-1 text-xs text-muted-foreground">
              {p.agentType ?? 'assistant'} · {fmtDateTime(p.at)}
              {p.conversationTitle ? ` · ${p.conversationTitle}` : ''}
            </div>
            <div className="whitespace-pre-wrap">{p.text}</div>
          </div>
        ))}
      </TabsContent>

      <TabsContent value="emails" className="pt-4 text-sm">
        <table className="data-table">
          <thead>
            <tr>
              <th>Sent</th>
              <th>Subject</th>
              <th>From</th>
              <th>Status</th>
              <th>Opens</th>
              <th>Clicks</th>
            </tr>
          </thead>
          <tbody>
            {data.emails.length === 0 && (
              <tr>
                <td colSpan={6} className="text-muted-foreground">
                  No emails sent yet
                </td>
              </tr>
            )}
            {data.emails.map((e) => (
              <tr key={`${e.origin}-${e.id}`}>
                <td className="whitespace-nowrap">{fmtDateTime(e.sentAt)}</td>
                <td>
                  {e.subject}
                  {e.isTest && <span className="ml-1 rounded bg-amber-100 px-1 text-xs text-amber-900">test</span>}
                  {e.sandbox && <span className="ml-1 rounded bg-amber-100 px-1 text-xs">sandbox</span>}
                </td>
                <td className="text-muted-foreground">
                  {e.origin === 'crm' ? `CRM · ${e.kind}` : `Facet Flow · ${e.kind}`}
                </td>
                <td>{e.status}</td>
                <td>{e.openCount}</td>
                <td>{e.clickCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TabsContent>

      <TabsContent value="audit" className="pt-4 text-sm">
        <table className="data-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Event</th>
              <th>Source</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {data.audit.length === 0 && (
              <tr>
                <td colSpan={4} className="text-muted-foreground">
                  No events yet
                </td>
              </tr>
            )}
            {data.audit.map((e) => (
              <tr key={e.id}>
                <td className="whitespace-nowrap">{fmtDateTime(e.occurredAt)}</td>
                <td>
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs">{e.type}</span>
                </td>
                <td className="text-muted-foreground">{e.source}</td>
                <td className="max-w-md break-words text-xs text-muted-foreground">
                  {e.type === 'note'
                    ? String(e.payload?.text ?? '')
                    : e.type === 'email_sent' && e.payload
                      ? `${e.payload.test ? '[test] ' : ''}${String(e.payload.templateName ?? e.payload.subject ?? '')}${e.payload.to ? ` → ${String(e.payload.to)}` : ''}`
                      : e.payload
                        ? JSON.stringify(e.payload)
                        : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TabsContent>

      <TabsContent value="flows" className="pt-4 text-sm">
        {data.flows.length === 0 ? (
          <p className="text-muted-foreground">This contact has not entered any flow yet. Runs appear here when an active flow's trigger fires (or via "Enrol" on a flow page).</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Flow</th>
                <th>Status</th>
                <th>Step</th>
                <th>Next run</th>
                <th>Started</th>
              </tr>
            </thead>
            <tbody>
              {data.flows.map((f) => (
                <tr key={f.id}>
                  <td>{f.flowId}</td>
                  <td>{f.status}</td>
                  <td>{f.stepIndex}</td>
                  <td>{fmtDateTime(f.nextRunAt)}</td>
                  <td>{fmtDateTime(f.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TabsContent>
    </Tabs>
  );
}
